import { apiClient } from '../client';
import type { DailySummary, Metric, MetricEntry } from '@/api/scorecards.api';
import type { DailyProgress } from '@/lib/scorecards/daily';
import type { Meeting } from './meetings.api';
import type { Rock } from './rocks.api';
import type { Todo } from './todos.api';

/**
 * Client for `/api/eos/my-day`. Stays in lockstep with
 * `server/src/modules/eos/my-day/my-day.service.ts` (Stream B Wave 4,
 * ADR-023) — one composite read that feeds the My Day dashboard.
 */

/** Raw to-do row — the my-day read skips the owner-name join (they're all mine). */
export type MyDayTodo = Omit<Todo, 'ownerMembership'>;

// The my-day read returns a lightweight rock summary: it swaps the milestones
// array for counts and omits the drawer-only depth fields (blocked banner,
// off-track count, dependencies) and the approval-workflow fields that the
// full rocks list carries.
export type MyDayRock = Omit<
  Rock,
  | 'milestones'
  | 'blocked'
  | 'offTrackMilestoneCount'
  | 'dependencies'
  | 'approvalState'
  | 'submittedAt'
  | 'integratorApprovedAt'
  | 'managerApprovedAt'
  | 'activatedAt'
  | 'rejectedAt'
  | 'rejectionReason'
  | 'pendingApprovers'
  | 'viewerCanApprove'
> & {
  milestonesTotal: number;
  milestonesDone: number;
};

// Keeps the scalar `seatId` but drops the `seat` relation — the my-day
// service selects entries only (no seat include), so the relation isn't
// present on this payload.
export type MyDayMetric = Omit<Metric, 'ownerEmployee' | 'seat'> & {
  latestEntry: MetricEntry | null;
  /** ADR-048: today's ring + streak. Null when the metric has no daily grain. */
  daily: DailySummary | null;
};

/** One department metric's pace today (ADR-048) — non-competitive: no people, no ranking. */
export interface DepartmentPaceMetric {
  id: string;
  title: string;
  unit: Metric['unit'];
  unitLabel: string | null;
  progress: DailyProgress;
}

export interface DepartmentPace {
  departmentName: string;
  /** The server's idea of today, in the reporting zone. */
  today: string;
  metrics: DepartmentPaceMetric[];
}

export type MyDayMeeting = Meeting & { seriesName: string | null };

export interface TeamPulse {
  openIssuesCount: number;
  offTrackRocksCount: number;
  teamOverdueTodosCount: number;
}

export interface MyDayPayload {
  myTodos: { items: MyDayTodo[]; overdueCount: number };
  myRocks: MyDayRock[];
  myMetrics: MyDayMetric[];
  upcomingMeetings: MyDayMeeting[];
  /** Present only for leads (department_lead, superadmin, manage_eos). */
  teamPulse: TeamPulse | null;
  /** ADR-048: my department's daily-goal metrics and where they stand today. Null when I have no department or it has none. */
  departmentPace: DepartmentPace | null;
  /** Headlines I authored that are queued (not yet attached to a meeting). */
  myHeadlinesQueued: number;
  /** Comms cards (Stream E / ADR-027). */
  comms: {
    unreadAnnouncements: {
      items: {
        id: string;
        title: string;
        requiresAck: boolean;
        publishedAt: string;
        authorNameSnapshot: string;
      }[];
      count: number;
    };
    recentShoutOuts: {
      items: {
        id: string;
        coreValue: string;
        message: string;
        giverNameSnapshot: string;
        recipientNameSnapshot: string;
        createdAt: string;
      }[];
    };
  };
}

export async function getMyDay(): Promise<MyDayPayload> {
  const res = await apiClient.get<{ data: MyDayPayload }>('/api/eos/my-day');
  return res.data.data;
}
